/**
 * src/components/documento/ContactoForm.tsx
 * =============================================================================
 * El formulario de contacto que vive al final del modal de ayuda.
 *
 * POR QUÉ AQUÍ Y NO EN UNA PÁGINA APARTE
 * --------------------------------------
 * Porque el momento en que alguien quiere escribir es exactamente el momento
 * en que ha abierto la ayuda y no ha encontrado lo que buscaba. Mandarle a
 * buscar una dirección de correo en otra pantalla es perderle: la mayoría no
 * escribe, se va, y ese fallo no lo conocemos nunca.
 *
 * EMPIEZA PLEGADO
 * ---------------
 * Un cuadro de texto siempre visible convierte la ayuda en un formulario, y la
 * ayuda es lo que la mayoría viene a leer. Es un enlace discreto hasta que
 * alguien lo pide.
 *
 * SOLO PARA QUIEN TIENE CUENTA
 * ----------------------------
 * Decisión del dueño, y además es lo que impide que esto sea un canal de spam.
 * A quien no la tiene no se le enseña nada: un formulario visible que al
 * enviarlo dice "regístrate" es peor que no ofrecerlo.
 * =============================================================================
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '@/lib/api'

type Estado = 'plegado' | 'escribiendo' | 'enviando' | 'enviado' | 'error'

export function ContactoForm() {
  const { t } = useTranslation()
  const [estado, setEstado] = useState<Estado>('plegado')
  const [mensaje, setMensaje] = useState('')

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    setEstado('enviando')
    try {
      await api('/contacto', {
        method: 'POST',
        // La página desde la que se escribe: "no me deja subir" no significa
        // lo mismo en la portada que dentro de un documento.
        body: { mensaje, desde: window.location.pathname },
      })
      setEstado('enviado')
      setMensaje('')
    } catch {
      setEstado('error')
    }
  }

  if (estado === 'plegado') {
    return (
      <button
        type="button"
        onClick={() => setEstado('escribiendo')}
        className="text-xs text-zinc-500 hover:text-zinc-900 underline underline-offset-4"
      >
        {t('visor.contacto.abrir', '¿Necesitas algo más? Escríbenos')}
      </button>
    )
  }

  if (estado === 'enviado') {
    return (
      <p className="text-xs text-zinc-600 leading-relaxed">
        {t('visor.contacto.enviado', 'Recibido. Te contestamos al correo de tu cuenta.')}
      </p>
    )
  }

  return (
    <form onSubmit={enviar} className="space-y-2">
      <textarea
        value={mensaje}
        onChange={(e) => setMensaje(e.target.value)}
        rows={4}
        maxLength={4000}
        autoFocus
        placeholder={t('visor.contacto.marcador', '¿Qué necesitas? Cuéntanoslo aquí.')}
        className="w-full text-xs rounded-md border border-zinc-200 px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-zinc-400"
      />
      <div className="flex items-center justify-between gap-3">
        {/* El error se dice aquí y no en un aviso flotante: quien acaba de
            escribir tres párrafos tiene que ver que NO se han ido, y verlo
            junto al texto que sigue estando en el cuadro. */}
        <p className="text-[11px] text-amber-800">
          {estado === 'error' ? t('visor.contacto.error', 'No se ha podido enviar. Inténtalo otra vez.') : ''}
        </p>
        <button
          type="submit"
          disabled={mensaje.trim().length < 10 || estado === 'enviando'}
          className="shrink-0 text-xs px-3 py-1.5 rounded-md bg-zinc-900 text-white disabled:opacity-40 disabled:cursor-default"
        >
          {estado === 'enviando'
            ? t('visor.contacto.enviando', 'Enviando…')
            : t('visor.contacto.enviar', 'Enviar')}
        </button>
      </div>
    </form>
  )
}
